import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { paths } from "~/router/paths";
import { Portal } from "~/helpers/portal";
import { useAccount } from "~/hooks/useAccount";
import { skipTrialDiscount } from "~/api/billing/requests";
import { toast } from "~/components/toast";
import { PlanStatus } from "~/types/account";
import * as S from "./styles";

const CLOSE_DELAY_MS = 60_000; // 1 minute before close button becomes active
const DISMISSED_KEY = "skip_trial_banner_dismissed";

export const SkipTrialBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { account, refetch } = useAccount();
  const [canClose, setCanClose] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem(DISMISSED_KEY) === "true";
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mountTimeRef = useRef(Date.now());

  useEffect(() => {
    const elapsed = Date.now() - mountTimeRef.current;
    const remaining = Math.max(CLOSE_DELAY_MS - elapsed, 0);
    const timer = setTimeout(() => setCanClose(true), remaining);
    return () => clearTimeout(timer);
  }, []);

  const isTrialing = useCallback(() => {
    if (!account?.active_subscription) return false;
    if (account.active_subscription.status !== PlanStatus.ACTIVE) return false;
    if (!account.active_subscription.trial_end_at) return false;
    const trialEnd = new Date(account.active_subscription.trial_end_at);
    return trialEnd > new Date();
  }, [account]);

  const shouldShow =
    !dismissed &&
    account &&
    !account.skip_trial_discount_redeemed &&
    (account.payment_provider === "stripe" || account.active_subscription?.payment_provider === "stripe") &&
    isTrialing();

  const handleClose = () => {
    if (!canClose) return;
    setDismissed(true);
    sessionStorage.setItem(DISMISSED_KEY, "true");
  };

  const handleClaimClick = () => {
    setShowConfirmModal(true);
  };

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(DISMISSED_KEY, "true");
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await skipTrialDiscount();
      setShowConfirmModal(false);
      dismiss();
      toast.success(t("skip_trial.success_toast"));
      await refetch();
    } catch (err: any) {
      const message =
        err?.response?.data?.error || t("skip_trial.error_generic");
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const lastFour = account?.stripe_card_digits;

  if (!shouldShow) return null;

  return (
    <>
      {/* Sticky Banner - bottom left */}
      {!showConfirmModal && (
        <Portal>
          <S.BannerWrapper>
            <S.CloseButton
              onClick={handleClose}
              disabled={!canClose}
              aria-disabled={!canClose}
              aria-label="Close"
              title={
                canClose
                  ? "Close"
                  : "Close button will be available shortly"
              }>
              &#x2715;
            </S.CloseButton>
            <S.Title>{t("skip_trial.banner_title")}</S.Title>
            <S.Description>{t("skip_trial.banner_description")}</S.Description>
            <S.ClaimButton onClick={handleClaimClick}>
              {t("skip_trial.claim_discount")}
            </S.ClaimButton>
          </S.BannerWrapper>
        </Portal>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <Portal>
          <S.ModalOverlay
            role="dialog"
            aria-modal="true"
            aria-labelledby="skip-trial-confirm-title"
            aria-describedby="skip-trial-confirm-desc">
            <S.ModalCard>
              <S.ModalCloseButton
                onClick={() => setShowConfirmModal(false)}
                aria-label="Close">
                &#x2715;
              </S.ModalCloseButton>

              <S.ModalTitle id="skip-trial-confirm-title">
                {t("skip_trial.confirm_title")}
              </S.ModalTitle>

              <S.ModalDescription id="skip-trial-confirm-desc">
                {t("skip_trial.confirm_description")}
              </S.ModalDescription>

              <S.TestimonialRow>
                <S.TestimonialAvatar />
                <S.TestimonialText>
                  {t("skip_trial.testimonial_quote")}
                </S.TestimonialText>
              </S.TestimonialRow>

              {lastFour && (
                <S.CardSection>
                  <S.CardInfo>
                    <S.CardLabel>{t("skip_trial.card_label")}</S.CardLabel>
                    <S.CardDigits>
                      {t("skip_trial.card_ending", { digits: lastFour })}
                    </S.CardDigits>
                  </S.CardInfo>
                  <S.ChangeCardLink
                    onClick={() => {
                      setShowConfirmModal(false);
                      navigate(paths.settings.account);
                    }}>
                    {t("skip_trial.change_card")}
                  </S.ChangeCardLink>
                </S.CardSection>
              )}

              <S.ConfirmButton
                onClick={handleConfirm}
                disabled={isSubmitting}>
                {isSubmitting
                  ? t("skip_trial.processing")
                  : t("skip_trial.confirm_button")}
              </S.ConfirmButton>
            </S.ModalCard>
          </S.ModalOverlay>
        </Portal>
      )}
    </>
  );
};
